import { Button, Typography } from "@mui/material";
import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";

interface CropperModalProps {
  image: string;
  onCropComplete: any;
  onCancel: () => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Canvas context not available");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) throw new Error("Canvas is empty");
      resolve(blob);
    }, "image/jpeg");
  });
}

const CropperModal: React.FC<CropperModalProps> = ({
  image,
  onCropComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const onCropCompleteInternal = useCallback(
    (_: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const onConfirm = async () => {
    if (!croppedAreaPixels) return;
    try {
      const croppedBlob = await getCroppedImg(image, croppedAreaPixels);
      onCropComplete(croppedBlob);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.7)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "50%",
          height: "60%",
          backgroundColor: "#333",
        }}
      >
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropCompleteInternal}
        />
      </div>

      <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
        <Button
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 3,
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "#9E9E9E",
            "&:hover": {
              backgroundColor: "#757575",
            },
            width: "200px",
          }}
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 3,
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "#1C4ED8",
            "&:hover": {
              backgroundColor: "#1642B3",
            },
            width: "200px",
          }}
          onClick={onConfirm}
        >
          Confirmar
        </Button>
      </div>

      <div
        style={{
          backgroundColor: "#D6E1FA",
          marginTop: "15px",
          padding: "5px",
          borderRadius: "10px",
        }}
      >
        <Typography style={{ fontSize: "10px", textAlign: "center" }}>
          Zoom
        </Typography>
        <input
          type="range"
          min={1}
          max={3}
          step={0.1}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
      </div>
    </div>
  );
};

export default CropperModal;
